import {
  ApplicationConfig,
  ENVIRONMENT_INITIALIZER,
  importProvidersFrom,
  inject,
  provideZonelessChangeDetection,
} from '@angular/core';
import { SocketService } from '@core/services/socket.service';
import { SessionService } from '@core/services/session.service';
import { ChatService } from '@features/layout/chat/services/chat.service';
import { CasesService } from '@features/games/cases/services/cases.service';
import { CaseBattlesService } from '@features/games/case-battles/case-battles.service';
import { LeaderboardService } from '@features/engagement/leaderboard/services/leaderboard.service';
import { LiveBetsService } from '@features/layout/live-bets/services/live-bets.service';
import { RewardsService } from '@features/engagement/rewards/services/rewards.service';
import { DepositService } from '@features/wallet/deposit/services/deposit.service';
import { GamesService } from '@core/services/games.service';
import { PlinkoService } from '@features/games/plinko/plinko.service';
import { JackpotHrService } from '@features/games/jackpot/jackpot-hr.service';
import { WheelService } from '@features/games/wheel/wheel.service';
import { TowersService } from '@features/games/towers/towers.service';
import { MinesService } from '@features/games/mines/mines.service';
import { AffiliatesService } from '@features/engagement/affiliates/services/affiliates.service';
import { GiveawaysService } from '@features/engagement/giveaways/giveaways.service';
import { provideRouter, withViewTransitions } from '@angular/router';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideHttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { routes } from './app.routes';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { environment } from '../environments/environment';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { provideSharedModals } from '@core/modals/shared-modals.providers';
import { provideCoinflipModals } from '@features/games/coinflip/coinflip-modals.providers';
import { provideKingModals } from '@features/games/king/king-modals.providers';
import { provideJackpotModals } from '@features/games/jackpot/jackpot-modals.providers';
import { provideDepositModals } from '@features/wallet/deposit/deposit-modals.providers';
import { provideWithdrawModals } from '@features/wallet/withdraw/withdraw-modals.providers';
import { provideAffiliatesModals } from '@features/engagement/affiliates/affiliates-modals.providers';
import { provideCaseBattlesModals } from '@features/games/case-battles/case-battles-modals.providers';
import { provideSupportModals } from '@features/pages/support/support-modals.providers';
import { provideRewardsModals } from '@features/engagement/rewards/rewards-modals.providers';
import { provideSupplyDropModals } from '@features/engagement/supply-drop/supply-drop-modals.providers';
import { provideUserModals } from '@features/user/user-modals.providers';
import { provideAdminModals } from '@features/admin/admin-modals.providers';
import { provideTriviaModals } from '@features/engagement/trivia/trivia-modals.providers';
import { provideRainModals } from '@features/engagement/rain/rain-modals.providers';
import { provideMinesModals } from '@features/games/mines/mines-modals.providers';
import { provideTowersModals } from '@features/games/towers/towers-modals.providers';
import { providePlinkoModals } from '@features/games/plinko/plinko-modals.providers';
import { provideUpgraderModals } from '@features/games/upgrader/upgrader-modals.providers';
import { provideWinModals } from '@features/games/shared/win-modal/win-modal.providers';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withViewTransitions()),
    provideHttpClient(),
    provideAngularSvgIcon(),
    provideAnimations(),
    provideToastr({
      positionClass: 'tf2hunt--toastr__bottom-right',
      toastClass: 'tf2hunt--toastr ngx-toastr',
      closeButton: true,
      autoDismiss: true,
      newestOnTop: false,
      maxOpened: 4,
    }),
    provideZonelessChangeDetection(),
    provideEnvironmentNgxMask(),
    importProvidersFrom(RecaptchaV3Module),
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptchaSiteKey,
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    provideCharts(withDefaultRegisterables()),
    ...provideSharedModals(),
    ...provideCoinflipModals(),
    ...provideKingModals(),
    ...provideJackpotModals(),
    ...provideDepositModals(),
    ...provideWithdrawModals(),
    ...provideAffiliatesModals(),
    ...provideCaseBattlesModals(),
    ...provideSupportModals(),
    ...provideRewardsModals(),
    ...provideSupplyDropModals(),
    ...provideUserModals(),
    ...provideAdminModals(),
    ...provideTriviaModals(),
    ...provideRainModals(),
    ...provideMinesModals(),
    ...provideTowersModals(),
    ...providePlinkoModals(),
    ...provideUpgraderModals(),
    ...provideWinModals(),
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        inject(SocketService);
        inject(SessionService);
        inject(ChatService);
        inject(CasesService);
        inject(CaseBattlesService);
        inject(LeaderboardService);
        inject(LiveBetsService);
        inject(RewardsService);
        inject(DepositService);
        inject(GamesService);
        inject(PlinkoService);
        inject(JackpotHrService);
        inject(WheelService);
        inject(TowersService);
        inject(MinesService);
        inject(AffiliatesService);
        inject(GiveawaysService);
      },
    },
  ],
};
